import React, { useState } from 'react';
import { Button, Form, ListGroup } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import {
  normalizeFormTabsPosition,
  type InternalFormTabsPosition,
} from '../changeInternalSubtaskFormTabs';

interface Props {
  tabs: string[];
  position?: InternalFormTabsPosition | string | null;
  canManage: boolean;
  saving?: boolean;
  onChange: (
    next: string[],
    extras?: { from?: string; to?: string; position?: InternalFormTabsPosition },
  ) => Promise<void> | void;
}

const ChangeInternalSubtaskTabsEditor: React.FC<Props> = ({
  tabs,
  position,
  canManage,
  saving = false,
  onChange,
}) => {
  const { showConfirm } = useModal();
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const tabsPosition = normalizeFormTabsPosition(position);

  const createTab = async () => {
    const name = draft.trim();
    if (!name) return;
    if (tabs.some((item) => item.toLowerCase() === name.toLowerCase())) return;
    setDraft('');
    await onChange([...tabs, name]);
  };

  const saveRename = async () => {
    if (!editing) return;
    const name = editName.trim();
    if (!name || name === editing) {
      setEditing(null);
      return;
    }
    const next = tabs.map((item) => (item === editing ? name : item));
    await onChange(next, { from: editing, to: name });
    setEditing(null);
  };

  const removeTab = async (name: string) => {
    const ok = await showConfirm({
      title: 'Excluir aba',
      message: `Excluir a aba «${name}»? Os campos dela voltam para o formulário principal.`,
      variant: 'danger',
      confirmLabel: 'Excluir aba',
      cancelLabel: 'Voltar',
    });
    if (!ok) return;
    await onChange(tabs.filter((item) => item !== name));
  };

  const moveTab = async (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= tabs.length) return;
    const next = [...tabs];
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    await onChange(next);
  };

  return (
    <div>
      <h6 className="mb-2">Abas do formulário</h6>
      <p className="small text-muted">
        Crie, reordene, renomeie ou exclua. Depois escolha a aba de cada campo no builder. Sem aba,
        o campo fica nas perguntas.
      </p>
      {canManage || tabs.length ? (
        <Form.Group className="mb-3" controlId="internal-form-tabs-position">
          <Form.Label className="small mb-1">Onde as abas aparecem</Form.Label>
          <Form.Select
            size="sm"
            value={tabsPosition}
            disabled={!canManage || saving}
            aria-label="Onde as abas aparecem"
            onChange={(event) =>
              void onChange(tabs, {
                position: normalizeFormTabsPosition(event.target.value),
              })
            }
          >
            <option value="after">Depois das perguntas</option>
            <option value="before">Antes das perguntas</option>
          </Form.Select>
        </Form.Group>
      ) : null}
      {tabs.length ? (
        <ListGroup className="mb-3">
          {tabs.map((name, index) => (
            <ListGroup.Item
              key={name}
              className="d-flex align-items-center gap-2 flex-wrap"
            >
              {editing === name ? (
                <>
                  <Form.Control
                    size="sm"
                    value={editName}
                    maxLength={255}
                    aria-label={`Novo nome da aba ${name}`}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                  <Button size="sm" disabled={saving} onClick={() => void saveRename()}>
                    Salvar nome
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setEditing(null)}
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <>
                  <strong className="flex-grow-1">{name}</strong>
                  {canManage ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={saving || index === 0}
                        aria-label={`Subir ${name}`}
                        onClick={() => void moveTab(index, -1)}
                      >
                        Subir
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={saving || index === tabs.length - 1}
                        aria-label={`Descer ${name}`}
                        onClick={() => void moveTab(index, 1)}
                      >
                        Descer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => {
                          setEditing(name);
                          setEditName(name);
                        }}
                      >
                        Renomear
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={saving}
                        onClick={() => void removeTab(name)}
                      >
                        Excluir
                      </Button>
                    </>
                  ) : null}
                </>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        <p className="small text-muted">Nenhuma aba ainda.</p>
      )}
      {canManage ? (
        <div className="d-flex flex-wrap gap-2">
          <Form.Control
            size="sm"
            style={{ maxWidth: 280 }}
            value={draft}
            maxLength={255}
            placeholder="Nome da nova aba"
            aria-label="Nome da nova aba"
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button size="sm" disabled={saving || !draft.trim()} onClick={() => void createTab()}>
            Criar aba
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default ChangeInternalSubtaskTabsEditor;
